/**
 * Display formatting for amounts in the card system. Separators and the sign
 * follow the app language, not the currency: es prints US$1.000,00 and
 * US$-2.000,00, en prints US$1,000.00 and -US$2,000.00, the same placement
 * the peso figure next to it gets.
 *
 * USD is written with an explicit "US$" in both languages. Intl would print a
 * bare "$" for USD under en-US, which on a screen full of pesos says the wrong
 * currency. CLP keeps the narrow "$" with no decimals. The UF is no ISO code, so
 * Intl would throw on it: it prints as "UF 4.500,25", up to two decimals with
 * the separators of the app language. Anything else falls back
 * to Intl with its ISO code. `fractionDigits` overrides the currency's own
 * digits for a caller that asks for a fixed precision.
 */
export function formatMoney(
  value: number,
  currency: string,
  locale: string,
  fractionDigits?: number,
): string {
  if (!Number.isFinite(value)) return '–';
  const fixed =
    fractionDigits === undefined
      ? {}
      : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits };
  if (currency === 'UF') {
    const figure = value.toLocaleString(locale, { maximumFractionDigits: 2, ...fixed });
    return `UF ${figure}`;
  }
  if (currency === 'USD') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...fixed,
    })
      .formatToParts(value)
      .map((part) => (part.type === 'currency' ? 'US$' : part.value))
      .join('');
  }
  if (currency === 'CLP') {
    return value.toLocaleString(locale, {
      style: 'currency',
      currency: 'CLP',
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
      ...fixed,
    });
  }
  try {
    return value.toLocaleString(locale, { style: 'currency', currency, ...fixed });
  } catch {
    // A malformed code (empty, two letters) makes Intl throw; the figure still prints.
    return value.toLocaleString(locale);
  }
}

/** How many decimals Intl gives the currency: 0 for CLP, 2 for USD, 4 for CLF. */
export function currencyFractionDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

const MINUS = '\u2212';

/**
 * A figure whose sign is part of the message, written as a typographic minus
 * or an optional plus ahead of the currency in both languages ("−$617.760",
 * "+$35.000"), instead of where Intl would place a hyphen.
 */
export function formatSignedMoney(
  value: number,
  currency: string,
  locale: string,
  { plus = false }: { plus?: boolean } = {},
): string {
  const magnitude = formatMoney(Math.abs(value), currency, locale);
  if (!Number.isFinite(value)) return magnitude;
  if (value < 0) return `${MINUS}${magnitude}`;
  return plus && value > 0 ? `+${magnitude}` : magnitude;
}

/** The observed rate as pesos per dollar, two decimals: "$969,85" under es, "$969.85" under en. */
export function formatRate(value: number, locale: string): string {
  return value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CLP',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
